// Minimal RFC4180-ish CSV parser. Handles:
//   - quoted fields with commas inside
//   - escaped quotes ("")
//   - CRLF, LF, CR line endings
//   - trailing newline (no phantom empty row)
//
// We deliberately don't pull in a CSV dependency — the parsing surface is
// tiny and avoiding deps keeps the bundle small and the privacy/audit
// story simple (every byte we ship is in this repo).

export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let i = 0;
  const n = text.length;

  while (i < n) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (i + 1 < n && text[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i++;
        continue;
      }
      field += c;
      i++;
      continue;
    }
    if (c === '"') {
      inQuotes = true;
      i++;
      continue;
    }
    if (c === ",") {
      row.push(field);
      field = "";
      i++;
      continue;
    }
    if (c === "\r") {
      // Handle CRLF as one separator
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
      if (i + 1 < n && text[i + 1] === "\n") i += 2;
      else i++;
      continue;
    }
    if (c === "\n") {
      row.push(field);
      field = "";
      rows.push(row);
      row = [];
      i++;
      continue;
    }
    field += c;
    i++;
  }
  // Flush trailing field/row if any
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  // Drop trailing fully-empty row (caused by a final newline in the source).
  while (rows.length > 0) {
    const last = rows[rows.length - 1];
    if (last.length === 1 && last[0] === "") rows.pop();
    else break;
  }
  return rows;
}

// Find the first parsed row whose joined cells contain every marker
// (case-insensitive). Mirrors _find_csv_header in scripts/loaders.py.
// Returns the index, or -1 if no row matches.
export function findHeaderIndex(rows: string[][], markers: readonly string[]): number {
  const lowered = markers.map((m) => m.toLowerCase());
  for (let i = 0; i < rows.length; i++) {
    const joined = rows[i].join(",").toLowerCase();
    if (lowered.every((m) => joined.includes(m))) return i;
  }
  return -1;
}

// Build dict rows given a header row and the data rows beneath it. Missing
// fields become "" — matches Python csv.DictReader semantics.
export function toDictRows(
  header: string[],
  dataRows: string[][],
): Array<Record<string, string>> {
  return dataRows.map((row) => {
    const obj: Record<string, string> = {};
    for (let i = 0; i < header.length; i++) {
      obj[header[i]] = row[i] ?? "";
    }
    return obj;
  });
}
